import api from '@/api/api.js'
import { DataLoadState } from '@/utils/storeUtils.js'
import { eventManager, HUNT_AREA_MEMBERS_CHANGED_EVENT } from '@/eventManager.js'

// Mutation names
const SET_HUNT_AREA_ID = 'setHuntAreaId'
const SET_HUNT_AREA_LOAD_STARTED = 'setHuntAreaLoadStarted'
const SET_HUNT_AREA_LOAD_FINISHED_SUCCESSFULLY = 'setHuntAreaLoadFinishedSuccessfully'
const SET_HUNT_AREA_LOAD_FAILED = 'setHuntAreaLoadFailed'
const RESET_HUNT_AREA = 'resetHuntArea'

const SET_MEMBERS_LOAD_STARTED = 'setMembersLoadStarted'
const SET_MEMBERS_LOAD_FINISHED_SUCCESSFULLY = 'setMembersLoadFinishedSuccessfully'
const SET_MEMBERS_LOAD_FAILED = 'setMembersLoadFailed'

const RESET_MEMBERS = 'resetMembers'

const SET_GAMECAM_CONFIGS_LOAD_STARTED = 'setGamecamConfigsLoadStarted'
const SET_GAMECAM_CONFIGS_LOAD_FINISHED_SUCCESSFULLY = 'setGamecamConfigsLoadFinishedSuccessfully'
const SET_GAMECAM_CONFIGS_LOAD_FAILED = 'setGamecamConfigsLoadFailed'

const SET_ACTIVITY_LOG_LOAD_STARTED = 'setActivityLogLoadStarted'
const SET_ACTIVITY_LOG_LOAD_FINISHED_SUCCESSFULLY = 'setActivityLogLoadFinishedSuccessfully'
const SET_ACTIVITY_LOG_LOAD_FAILED = 'setActivityLogLoadFailed'

// Getter names
export const IS_LOADING_HUNT_AREA = 'isLoadingHuntArea'
export const GET_HUNT_AREA = 'getHuntArea'
export const GET_HUNT_AREA_LOAD_ERROR = 'getHuntAreaLoadError'

export const IS_LOADING_MEMBERS = 'isLoadingMembers'
export const GET_MEMBERS = 'getMembers'
export const GET_MEMBERS_LOAD_ERROR = 'getMembersLoadError'
export const HAS_EMPTY_MEMBERS_LOAD_RESULT = 'hasEmptyMembersLoadResult'

export const IS_LOADING_GAMECAM_CONFIGS = 'isLoadingGamecamConfigs'
export const GET_GAMECAM_CONFIGS = 'getGamecamConfigs'
export const GET_GAMECAM_CONFIGS_LOAD_ERROR = 'getGamecamConfigsLoadError'
export const HAS_EMPTY_GAMECAM_CONFIGS_LOAD_RESULT = 'hasEmptyGamecamConfigsLoadResult'

export const IS_LOADING_ACTIVITY_LOG = 'isLoadingActivityLog'
export const GET_ACTIVITY_LOG = 'getActivityLog'
export const GET_ACTIVITY_LOG_LOAD_ERROR = 'getActivityLogLoadError'
export const HAS_EMPTY_ACTIVITY_LOG_LOAD_RESULT = 'hasEmptyActivityLogLoadResult'

// Action name
export const LOAD_HUNT_AREA_WITH_ID = 'loadHuntAreaWithId'
export const SET_HUNT_AREA = 'setHuntArea'
export const LOAD_MEMBERS = 'loadMembers'
export const REMOVE_MEMBER_WITH_ID = 'removeMemberWithId'
export const UPDATE_ADMIN = 'updateAmin'
export const LOAD_GAMECAM_CONFIGS = 'loadGamecamConfigs'
export const ADD_MEMBER = 'addMember'
export const LOAD_ACTIVITY_LOG = 'loadActivityLog'

// Util functions
function sortMemberByName (member, secondMember) {
  const name = `${member.firstName} ${member.lastName}`
  const secondMemberName = `${secondMember.firstName} ${secondMember.lastName}`
  return name.localeCompare(secondMemberName)
}

// Hunt area store implementation
export const HuntAreaStore = {
  namespaced: true,

  state: () => ({ // state must be a function to allow reuse of this store
    huntAreaId: null,
    huntAreaState: new DataLoadState(),
    membersState: new DataLoadState(),
    gamecamConfigsState: new DataLoadState(),
    activityLogState: new DataLoadState()
  }),

  mutations: {
    [SET_HUNT_AREA_ID] (state, { huntAreaId }) {
      state.huntAreaId = huntAreaId
    },

    [SET_HUNT_AREA] (state, { huntArea }) {
      if (!huntArea || !huntArea.id) {
        state.huntAreaState = new DataLoadState()
        state.membersState = new DataLoadState()
        state.gamecamConfigsState = new DataLoadState()
        state.activityLogState = new DataLoadState()
        return
      }

      state.huntAreaState = new DataLoadState(huntArea.id).stateForLoadFinished(huntArea)
    },

    [SET_HUNT_AREA_LOAD_STARTED] (state, { huntAreaId }) {
      const dataLoadState = new DataLoadState(huntAreaId)
      state.huntAreaState = dataLoadState.stateForLoadStarted()
    },

    [SET_HUNT_AREA_LOAD_FINISHED_SUCCESSFULLY] (state, { huntArea }) {
      if (huntArea.id !== state.huntAreaState.id) {
        // Hunt area load is stale, ignore
        return
      }

      state.huntAreaState = state.huntAreaState.stateForLoadFinished(huntArea)
    },

    [SET_HUNT_AREA_LOAD_FAILED] (state, { huntAreaId, error }) {
      if (huntAreaId !== state.huntAreaState.id) {
        // Hunt area load is stale, ignore
        return
      }

      state.huntAreaState = state.huntAreaState.stateForLoadFailed(error)
    },

    [RESET_HUNT_AREA] (state, { huntAreaId }) {
      if (huntAreaId !== state.huntAreaState.id) {
        // Hunt area has changed, ignore
        return
      }

      state.huntAreaState = new DataLoadState()
    },

    [SET_MEMBERS_LOAD_STARTED] (state, { huntAreaId }) {
      const dataLoadState = new DataLoadState(huntAreaId)
      state.membersState = dataLoadState.stateForLoadStarted()
    },

    [SET_MEMBERS_LOAD_FINISHED_SUCCESSFULLY] (state, { huntAreaId, members }) {
      if (huntAreaId !== state.membersState.id) {
        // Members load is stale, ignore
        return
      }

      state.membersState = state.membersState.stateForLoadFinished(members)
    },

    [SET_MEMBERS_LOAD_FAILED] (state, { huntAreaId, error }) {
      if (huntAreaId !== state.membersState.id) {
        // Members load is stale, ignore
        return
      }

      state.membersState = state.membersState.stateForLoadFailed(error)
    },

    [RESET_MEMBERS] (state) {
      state.membersState = new DataLoadState()
    },

    [SET_GAMECAM_CONFIGS_LOAD_STARTED] (state, { huntAreaId }) {
      const dataLoadState = new DataLoadState(huntAreaId)
      state.gamecamConfigsState = dataLoadState.stateForLoadStarted()
    },

    [SET_GAMECAM_CONFIGS_LOAD_FINISHED_SUCCESSFULLY] (state, { huntAreaId, gamecamConfigs }) {
      if (huntAreaId !== state.gamecamConfigsState.id) {
        // Gamecam configs load is stale, ignore
        return
      }

      state.gamecamConfigsState = state.gamecamConfigsState.stateForLoadFinished(gamecamConfigs)
    },

    [SET_GAMECAM_CONFIGS_LOAD_FAILED] (state, { huntAreaId, error }) {
      if (huntAreaId !== state.gamecamConfigsState.id) {
        // Gamecam configs load is stale, ignore
        return
      }

      state.gamecamConfigsState = state.gamecamConfigsState.stateForLoadFailed(error)
    },

    [SET_ACTIVITY_LOG_LOAD_STARTED] (state, { huntAreaId }) {
      const dataLoadState = new DataLoadState(huntAreaId)
      state.activityLogState = dataLoadState.stateForLoadStarted()
    },

    [SET_ACTIVITY_LOG_LOAD_FINISHED_SUCCESSFULLY] (state, { huntAreaId, activityLog }) {
      if (huntAreaId !== state.activityLogState.id) {
        // Activity log load is stale, ignore
        return
      }

      state.activityLogState = state.activityLogState.stateForLoadFinished(activityLog)
    },

    [SET_ACTIVITY_LOG_LOAD_FAILED] (state, { huntAreaId, error }) {
      if (huntAreaId !== state.activityLogState.id) {
        // Activity log load is stale, ignore
        return
      }

      state.activityLogState = state.activityLogState.stateForLoadFailed(error)
    }
  },

  getters: {
    [IS_LOADING_HUNT_AREA]: state => {
      return state.huntAreaState.status.loading
    },

    [GET_HUNT_AREA]: state => {
      return state.huntAreaState.data
    },

    [GET_HUNT_AREA_LOAD_ERROR]: state => {
      return state.huntAreaState.status.error
    },

    [IS_LOADING_MEMBERS]: state => {
      return state.membersState.status.loading
    },

    [GET_MEMBERS]: state => {
      return state.membersState.data
    },

    [GET_MEMBERS_LOAD_ERROR]: state => {
      return state.membersState.status.error
    },

    [HAS_EMPTY_MEMBERS_LOAD_RESULT]: state => {
      return state.membersState.status.success && state.membersState.data.length === 0
    },

    [IS_LOADING_GAMECAM_CONFIGS]: state => {
      return state.gamecamConfigsState.status.loading
    },

    [GET_GAMECAM_CONFIGS]: state => {
      return state.gamecamConfigsState.data
    },

    [GET_GAMECAM_CONFIGS_LOAD_ERROR]: state => {
      return state.gamecamConfigsState.status.error
    },

    [HAS_EMPTY_GAMECAM_CONFIGS_LOAD_RESULT]: state => {
      return state.gamecamConfigsState.status.success && state.gamecamConfigsState.data.length === 0
    },

    [IS_LOADING_ACTIVITY_LOG]: state => {
      return state.activityLogState.status.loading
    },

    [GET_ACTIVITY_LOG]: state => {
      return state.activityLogState.data
    },

    [GET_ACTIVITY_LOG_LOAD_ERROR]: state => {
      return state.activityLogState.status.error
    },

    [HAS_EMPTY_ACTIVITY_LOG_LOAD_RESULT]: state => {
      return state.activityLogState.status.success && state.activityLogState.data.length === 0
    }
  },

  actions: {
    [LOAD_HUNT_AREA_WITH_ID]: async ({ commit, state }, { huntAreaId }) => {
      if (!huntAreaId) {
        return
      }

      if (huntAreaId === state.huntAreaState.id) {
        // Hunt area load has already started
        return
      }

      commit(SET_HUNT_AREA_ID, { huntAreaId })
      commit(SET_HUNT_AREA_LOAD_STARTED, { huntAreaId })

      try {
        const response = await api.get(`admin/huntareas/${huntAreaId}`)
        const huntArea = response.data

        commit(SET_HUNT_AREA_LOAD_FINISHED_SUCCESSFULLY, { huntArea })
      } catch (error) {
        commit(SET_HUNT_AREA_LOAD_FAILED, { huntAreaId, error })
      }
    },

    [SET_HUNT_AREA]: ({ commit }, { huntArea }) => {
      var huntAreaId = null
      if (huntArea) {
        huntAreaId = huntArea.id || null
      }

      commit(SET_HUNT_AREA_ID, { huntAreaId })
      commit(SET_HUNT_AREA, { huntArea })
    },

    [LOAD_MEMBERS]: async ({ commit, state }) => {
      const huntAreaId = state.huntAreaId
      if (!huntAreaId) {
        return
      }

      if (huntAreaId === state.membersState.id) {
        // Members load has already started
        return
      }

      commit(SET_MEMBERS_LOAD_STARTED, { huntAreaId })

      try {
        const response = await api.get(
          `admin/huntareas/${huntAreaId}/members`
        )

        const members = response.data.slice(0).sort(sortMemberByName)
        commit(SET_MEMBERS_LOAD_FINISHED_SUCCESSFULLY, { huntAreaId, members })
      } catch (error) {
        commit(SET_MEMBERS_LOAD_FAILED, { huntAreaId, error })
      }
    },

    [REMOVE_MEMBER_WITH_ID]: ({ commit, dispatch }, { huntAreaId, userId }) => {
      return api.delete(
        `admin/huntareas/${huntAreaId}/members/${userId}`
      ).then(() => {
        // Always reload members to make sure the list is correct.
        commit(RESET_MEMBERS)
        dispatch(LOAD_MEMBERS)

        // Emit event to allow other stores to reload or delete remove stale data
        eventManager.$emit(HUNT_AREA_MEMBERS_CHANGED_EVENT, { huntAreaId, userId })
      })
    },

    [UPDATE_ADMIN]: ({ commit, state, dispatch }, { newAdminUserId }) => {
      const huntAreaId = state.huntAreaId
      if (!huntAreaId) {
        return
      }

      return api.post(`admin/huntareas/${huntAreaId}/changeAdmin`, { newAdminUserId }).then(() => {
        // Reload hunt area data after admin has been updated
        commit(RESET_HUNT_AREA, { huntAreaId })
        dispatch(LOAD_HUNT_AREA_WITH_ID, { huntAreaId })
      })
    },

    [LOAD_GAMECAM_CONFIGS]: async ({ commit, state }) => {
      const huntAreaId = state.huntAreaId
      if (!huntAreaId) {
        return
      }

      if (huntAreaId === state.gamecamConfigsState.id) {
        // Members load has already started
        return
      }

      commit(SET_GAMECAM_CONFIGS_LOAD_STARTED, { huntAreaId })

      try {
        const response = await api.get(
          `admin/huntareas/${huntAreaId}/gamecamConfigs`
        )

        const gamecamConfigs = response.data
        commit(SET_GAMECAM_CONFIGS_LOAD_FINISHED_SUCCESSFULLY, { huntAreaId, gamecamConfigs })
      } catch (error) {
        commit(SET_GAMECAM_CONFIGS_LOAD_FAILED, { huntAreaId, error })
      }
    },

    [ADD_MEMBER]: ({ commit, state, dispatch }, { userQuery }) => {
      const huntAreaId = state.huntAreaId
      if (!huntAreaId) {
        return
      }

      return api.post(`admin/huntareas/${huntAreaId}/members`, { userQuery }).then(() => {
        // Always reload members to make sure the list is correct.
        commit(RESET_MEMBERS)
        dispatch(LOAD_MEMBERS)

        // Emit event to allow other stores to reload or delete remove stale data
        eventManager.$emit(HUNT_AREA_MEMBERS_CHANGED_EVENT, { huntAreaId })
      })
    },

    [LOAD_ACTIVITY_LOG]: async ({ commit, state }) => {
      const huntAreaId = state.huntAreaId
      if (!huntAreaId) {
        return
      }

      if (huntAreaId === state.activityLogState.id) {
        // Activity log load has already started
        return
      }

      commit(SET_ACTIVITY_LOG_LOAD_STARTED, { huntAreaId })

      try {
        const response = await api.get(
          `admin/huntareas/${huntAreaId}/activityLog`
        )

        const activityLog = response.data
        commit(SET_ACTIVITY_LOG_LOAD_FINISHED_SUCCESSFULLY, { huntAreaId, activityLog })
      } catch (error) {
        commit(SET_ACTIVITY_LOG_LOAD_FAILED, { huntAreaId, error })
      }
    }
  }
}
